import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HttpService } from './http.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  AUTHTOKEN_KEY: string = 'teainntech-user-token';
  AUTHUSER_KEY: string = 'teainntech-user-user';

  /**
   * =======================
   * Reactive User State
   * =======================
   * */

  private readonly userSubject = new BehaviorSubject<any | null>(
    this.readUser(),
  );

  /** Current user stream (null when logged out) */
  readonly user$: Observable<any | null> = this.userSubject.asObservable();

  constructor(
    private httpService: HttpService,
    private router: Router,
  ) {
    // Keep user in sync when it changes in another browser tab
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((e) => e.key === this.AUTHUSER_KEY))
      .subscribe(() => this.userSubject.next(this.readUser()));
  }

  /**
   * =======================
   * User Auth API Functions
   * =======================
   * */

  userRegister(payload: any) {
    return this.httpService.post('v1/auth/register/submit', payload);
  }

  sellerRegister(payload: any) {
    return this.httpService.postFormData(
      'v1/auth/register/seller/submit',
      payload,
    );
  }

  userLogin(payload: any) {
    return this.httpService.post('v1/auth/login/submit', payload);
  }

  userForgotPassword(payload: any) {
    return this.httpService.post('v1/auth/password/reset', payload);
  }

  userSuccessLogin(data: any, rememberme: any = true) {
    if (rememberme === true) {
      localStorage.setItem(this.AUTHTOKEN_KEY, data.token);
      localStorage.setItem(this.AUTHUSER_KEY, JSON.stringify(data.user));
      this.userSubject.next(data.user);
    }

    switch (data.user.role) {
      case 'broker':
        this.router.navigate(['/dashboard/seller/offer-sheet/generate']);
        break;
      case 'seller':
      case 'buyer':
        this.router.navigate(['/dashboard/buyer/offer-sheet/search']);
        break;
      default:
        this.router.navigate(['/dashboard']);
        break;
    }
  }

  userLogout() {
    let userdetails = this.getUserSessionDetails();

    localStorage.removeItem(this.AUTHTOKEN_KEY);
    localStorage.removeItem(this.AUTHUSER_KEY);
    this.userSubject.next(null);

    this.router.navigate([`/${userdetails?.role}/login`]);
  }

  userLoggedIn() {
    return !!this.getUserToken();
  }

  getUserToken() {
    let token = localStorage.getItem(this.AUTHTOKEN_KEY);

    return token ?? null;
  }

  getUserSessionDetails() {
    return this.userSubject.value;
  }

  setUserSessionDetails(data: any) {
    if (localStorage.getItem(this.AUTHUSER_KEY)) {
      localStorage.setItem(this.AUTHUSER_KEY, JSON.stringify(data.user));
      this.userSubject.next(data.user);
    }
  }

  getUserAccount() {
    return this.httpService.post('v1/web/user/getaccount', {});
  }

  /**
   * ====================
   * Common API Functions
   * ====================
   * */

  getToken() {
    if (this.getUserToken()) {
      return this.getUserToken();
    } else {
      return null;
    }
  }

  /**
   * =================
   * OTP API Functions
   * =================
   * */

  otpInit(payload: any) {
    return this.httpService.post('v1/auth/otp/init', payload);
  }

  otpVerify(payload: any) {
    return this.httpService.post('v1/auth/otp/verify', payload);
  }

  otpResend(payload: any) {
    return this.httpService.post('v1/auth/otp/resend', payload);
  }

  getGstinDetails(payload: any) {
    return this.httpService.post('v1/auth/gst-detail', payload);
  }

  /**
   * =================
   * Storage Helper
   * =================
   * */

  private readUser(): any | null {
    try {
      const raw = localStorage.getItem(this.AUTHUSER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  checkuniqueEmail(payload: any) {
    return this.httpService.post('v1/auth/check-unique-email', payload);
  }
}
